import { ApiEndpoint } from "../types";

/**
 * Utility functions for detecting and handling CM Table endpoints.
 * Centralises logic previously scattered across regex matches.
 */

/**
 * Detects if an endpoint is a v2 CM Table endpoint.
 */
export const isCmTableEndpoint = (endpoint: ApiEndpoint | null | undefined): boolean => {
  if (!endpoint) return false;
  
  // Primary check: the new explicit dataSourceType
  if (endpoint.dataSourceType === "cm-table") return true;

  // Fallback for transition: check the nested v2 URL pattern
  return (
    !!endpoint.api_endpoint?.startsWith("v2/tables/") &&
    endpoint.api_endpoint.endsWith("/rows/")
  );
};

/**
 * Extracts the table slug from a CM Table endpoint.
 * Since the introduction of tableSlug, we prefer it over overloading tableName.
 */
export const getCmTableSlugFromEndpoint = (endpoint: ApiEndpoint | null | undefined): string | null => {
  if (!isCmTableEndpoint(endpoint)) return null;
  return endpoint?.tableSlug || endpoint?.tableName || null;
};

/**
 * Canonical React Query key for a single CM Table row (used by useSelectedEntity's
 * CM Table fast-path). Row ids reach call sites in mixed types — strings from URL
 * deep-links (?entity_id=44), numbers from API responses / map clicks — so this
 * always coerces to string. Use this everywhere a cm-table-row key is built; calling
 * it with a differently-typed id than the live cache entry silently writes a ghost
 * entry that no subscriber reads.
 */
export const getCmTableRowQueryKey = (tableSlug: string | null | undefined, id: string | number | null | undefined) => [
  "cm-table-row",
  tableSlug,
  String(id)
];
